import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { LoginPage } from '@/pages/login'
import { DashboardPage } from '@/pages/dashboard'
import { PolixAIV2Page } from '@/pages/polix-ai-v2-page'
import { PoliciesPage } from '@/pages/policies'
import { PolicyDetailPage } from '@/pages/policy-detail'
import { PolicyEditPage } from '@/pages/policy-edit'
import { PolicyNewPage } from '@/pages/policy-new'
import { OAuthCallbackPage } from '@/pages/oauth-callback'
import { AttributesPage } from '@/pages/attributes'
import { ResourceTagsPage } from '@/pages/resource-tags'
import { ResourceTagDetailPage } from '@/pages/resource-tag-detail'
import { ClassificationsPage } from '@/pages/classifications'
import { DataCatalogPage } from '@/pages/data-catalog'
import { AnalyticsPage } from '@/pages/analytics'
import { InfrastructurePage } from '@/pages/infrastructure'
import { LaunchpadPage } from '@/pages/launchpad'
import { ProtectedRoute } from '@/components/auth/protected-route'
import { PACLayout } from '@/components/layout/pac-layout'
import { PolixAIV2Layout } from '@/components/layout/polix-ai-v2-layout'
import { DiscoveryLayout } from '@/components/layout/discovery-layout'
import { IdentityLayout } from '@/components/layout/identity-layout'
import { IdentityDashboardPage } from '@/pages/identity/dashboard'
import { SubjectsPage } from '@/pages/identity/subjects'
import { SubjectDetailPage } from '@/pages/identity/subject-detail'
import { ConnectionsPage as IdentityConnectionsPage } from '@/pages/identity/connections'
import { ConnectionNewPage as IdentityConnectionNewPage } from '@/pages/identity/connection-new'
import { ConnectionDetailPage as IdentityConnectionDetailPage } from '@/pages/identity/connection-detail'
import { IdentityAuditLogsPage } from '@/pages/identity/audit-logs'
import { DiscoveryDashboardPage } from '@/pages/discovery/dashboard'
import { DiscoveryConnectionsPage } from '@/pages/discovery/connections'
import { ConnectionNewPage } from '@/pages/discovery/connection-new'
import { ConnectionDetailPage } from '@/pages/discovery/connection-detail'
import { DiscoveryScansPage } from '@/pages/discovery/scans'
import { ScanResultsPage } from '@/pages/discovery/scan-results'
import { DocsLayout } from '@/components/layout/docs-layout'
import { GettingStartedPage } from '@/pages/docs/getting-started'
import { AbacPage } from '@/pages/docs/abac'
import { XACMLPage } from '@/pages/docs/xacml'
import { AttributesPage as DocsAttributesPage } from '@/pages/docs/attributes'
import { PoliciesPage as DocsPoliciesPage } from '@/pages/docs/policies'
import { EvaluationPage } from '@/pages/docs/evaluation'
import { ArchitecturePage } from '@/pages/docs/architecture'
import { UsersPage } from '@/pages/users'
import { SettingsPage } from '@/pages/settings'
import { AdminRoute } from '@/components/auth/admin-route'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 3,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
})

/**
 * Router configuration
 *
 * Platform shell routes:
 * - / - Launchpad (app selector)
 * - /login - Authentication
 *
 * Policy Admin Console (PAC):
 * - /pac/* - All policy administration routes
 *
 * Polix AI (cross-app):
 * - /polix-ai - AI assistant
 *
 * Backwards compatibility:
 * - Old flat routes redirect to /pac/* equivalents
 */
const router = createBrowserRouter([
  // Public routes
  { path: '/login', element: <LoginPage /> },
  { path: '/oauth/callback', element: <OAuthCallbackPage /> },

  // Platform shell
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <LaunchpadPage />
      </ProtectedRoute>
    ),
  },

  // Policy Admin Console
  {
    path: '/pac',
    element: (
      <ProtectedRoute>
        <PACLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'policies', element: <PoliciesPage /> },
      { path: 'policies/new', element: <PolicyNewPage /> },
      { path: 'policies/:id', element: <PolicyDetailPage /> },
      { path: 'policies/:id/edit', element: <PolicyEditPage /> },
      { path: 'attributes', element: <AttributesPage /> },
      { path: 'resource-tags', element: <ResourceTagsPage /> },
      { path: 'resource-tags/:id', element: <ResourceTagDetailPage /> },
      { path: 'applications', element: <Navigate to="/discovery/connections" replace /> },
      { path: 'applications/:id', element: <Navigate to="/discovery/connections" replace /> },
      { path: 'classifications', element: <Navigate to="/discovery/classifications" replace /> },
      { path: 'data-catalog', element: <Navigate to="/discovery/data-catalog" replace /> },
      { path: 'analytics', element: <AnalyticsPage /> },
      { path: 'audit-logs', element: <Navigate to="/pac/analytics?tab=audit-logs" replace /> },
      { path: 'decision-logs', element: <Navigate to="/pac/analytics?tab=access-decisions" replace /> },
      { path: 'reports', element: <Navigate to="/pac/analytics?tab=reports" replace /> },
      { path: 'security-analytics', element: <Navigate to="/pac/analytics" replace /> },
      { path: 'infrastructure', element: <InfrastructurePage /> },
      {
        path: 'users',
        element: (
          <AdminRoute>
            <UsersPage />
          </AdminRoute>
        ),
      },
      { path: 'settings', element: <SettingsPage /> },
    ],
  },

  // Data Discovery
  {
    path: '/discovery',
    element: (
      <ProtectedRoute>
        <DiscoveryLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: 'dashboard', element: <DiscoveryDashboardPage /> },
      { path: 'connections', element: <DiscoveryConnectionsPage /> },
      { path: 'connections/new', element: <ConnectionNewPage /> },
      { path: 'connections/:id', element: <ConnectionDetailPage /> },
      { path: 'scans', element: <DiscoveryScansPage /> },
      { path: 'scans/:id/results', element: <ScanResultsPage /> },
      { path: 'classifications', element: <ClassificationsPage /> },
      { path: 'data-catalog', element: <DataCatalogPage /> },
    ],
  },

  // Identity Store
  {
    path: '/identity',
    element: (
      <ProtectedRoute>
        <IdentityLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: 'dashboard', element: <IdentityDashboardPage /> },
      { path: 'subjects', element: <SubjectsPage /> },
      { path: 'subjects/:id', element: <SubjectDetailPage /> },
      { path: 'connections', element: <IdentityConnectionsPage /> },
      { path: 'connections/new', element: <IdentityConnectionNewPage /> },
      { path: 'connections/:id', element: <IdentityConnectionDetailPage /> },
      { path: 'audit-logs', element: <IdentityAuditLogsPage /> },
    ],
  },

  // Polix AI (Vercel AI SDK)
  {
    path: '/polix-ai',
    element: (
      <ProtectedRoute>
        <PolixAIV2Layout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <PolixAIV2Page /> },
      { path: ':conversationId', element: <PolixAIV2Page /> },
    ],
  },

  // Documentation
  {
    path: '/docs',
    element: (
      <ProtectedRoute>
        <DocsLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="getting-started" replace /> },
      { path: 'getting-started', element: <GettingStartedPage /> },
      { path: 'abac', element: <AbacPage /> },
      { path: 'xacml', element: <XACMLPage /> },
      { path: 'attributes', element: <DocsAttributesPage /> },
      { path: 'policies', element: <DocsPoliciesPage /> },
      { path: 'evaluation', element: <EvaluationPage /> },
      { path: 'architecture', element: <ArchitecturePage /> },
    ],
  },

  // Backwards compat: old v2 path redirects to /polix-ai
  { path: '/polix-ai-v2', element: <Navigate to="/polix-ai" replace /> },
  { path: '/polix-ai-v2/:conversationId', element: <Navigate to="/polix-ai" replace /> },

  // Backwards compatibility redirects
  { path: '/dashboard', element: <Navigate to="/pac/dashboard" replace /> },
  { path: '/policies', element: <Navigate to="/pac/policies" replace /> },
  { path: '/policies/new', element: <Navigate to="/pac/policies/new" replace /> },
  { path: '/policies/:id', element: <Navigate to="/pac/policies/:id" replace /> },
  { path: '/policies/:id/edit', element: <Navigate to="/pac/policies/:id/edit" replace /> },
  { path: '/attributes', element: <Navigate to="/pac/attributes" replace /> },
  { path: '/resource-tags', element: <Navigate to="/pac/resource-tags" replace /> },
  { path: '/resource-tags/new', element: <Navigate to="/pac/resource-tags/new" replace /> },
  { path: '/resource-tags/:id', element: <Navigate to="/pac/resource-tags/:id" replace /> },
  { path: '/applications', element: <Navigate to="/discovery/connections" replace /> },
  { path: '/applications/:id', element: <Navigate to="/discovery/connections" replace /> },
  { path: '/classifications', element: <Navigate to="/discovery/classifications" replace /> },
  { path: '/data-catalog', element: <Navigate to="/discovery/data-catalog" replace /> },
  { path: '/audit-logs', element: <Navigate to="/pac/analytics?tab=audit-logs" replace /> },
  { path: '/decision-logs', element: <Navigate to="/pac/analytics?tab=access-decisions" replace /> },
  { path: '/reports', element: <Navigate to="/pac/analytics?tab=reports" replace /> },
  { path: '/security-analytics', element: <Navigate to="/pac/analytics" replace /> },
  { path: '/policy-crafting', element: <Navigate to="/polix-ai" replace /> },
  { path: '/policy-crafting/:conversationId', element: <Navigate to="/polix-ai" replace /> },

  // Catch-all
  { path: '*', element: <Navigate to="/" replace /> },
])

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster position="top-right" richColors />
    </QueryClientProvider>
  )
}

export default App
